import { AuthenticationService } from "@/services/auth";
import type { RouteRecordRaw } from "vue-router";

export type RouteMeta = {
  myProperty?: string;
  requiresAuth?: boolean;
};

export type RouteRecordRawCustom = RouteRecordRaw & {
  meta?: RouteMeta;
};

export type RouterAuthService = Pick<AuthenticationService, "login" | "isAuthenticated" | "getAuthenticatedUser">;

export enum Features {
  DASHBOARD = "DASHBOARD",
  SHIPMENTS = "SHIPMENTS",
  DND = "DND",
  PORT_CONGESTION = "PORT_CONGESTION",
  AIR_SHIPMENTS = "AIR_SHIPMENTS",
  DND_EXPORT = "DND_EXPORT",
  EMISSIONS = "EMISSIONS",
  DND_LEGACY = "DND_LEGACY",
  RISK_MANAGEMENT = "RISK_MANAGEMENT",
  REPORTING = "REPORTING",
  CARRIER_PERFORMANCE = "CARRIER_PERFORMANCE",
  UAM = "UAM",
  INVOICE_AUDIT = "INVOICE_AUDIT",
  METERING = "METERING",
  BOOKINGS = "BOOKINGS",
  TRACK_AND_TRACE = "TRACK_AND_TRACE"
}

export enum AccessRoleTypes {
  READ = "READ",
  WRITE = "WRITE"
}

export type FeatureConfig = {
  userFlag: string | null;
  envFlag: string | null;
  requiredRoles: string[] | null;
};

export type FeatureConfigMap = {
  [key in Features]: FeatureConfig;
};

export const FEATURE_CONFIG: FeatureConfigMap = {
  [Features.DASHBOARD]: {
    userFlag: "uiEnabled",
    envFlag: null,
    requiredRoles: ["ISCE.read", "isce.controltower.read"]
  },
  [Features.SHIPMENTS]: {
    userFlag: "uiEnabled",
    envFlag: null,
    requiredRoles: ["ISCE.read", "ISCE.write"]
  },
  [Features.DND]: {
    userFlag: "dndEnabled",
    envFlag: null,
    requiredRoles: ["iscednd.read"]
  },
  [Features.PORT_CONGESTION]: {
    userFlag: "disruptionEnabled",
    envFlag: "VITE_IS_PORT_CONGESTION_ENABLED",
    requiredRoles: ["iscecongestion.read"]
  },
  [Features.AIR_SHIPMENTS]: {
    userFlag: "airEnabled",
    envFlag: "vite_is_air_shipments_enabled",
    requiredRoles: ["ISCE.read", "ISCE.write"]
  },
  [Features.DND_EXPORT]: {
    userFlag: "dndExportEnabled",
    envFlag: null,
    requiredRoles: ["iscednd.read"]
  },
  [Features.EMISSIONS]: {
    userFlag: "emissionEnabled",
    envFlag: "VITE_IS_EMISSIONS_ENABLED",
    requiredRoles: ["ISCE.read", "isceemission.read", "isceemission.write"]
  },
  [Features.DND_LEGACY]: {
    userFlag: "dndEnabled",
    envFlag: "VITE_IS_DND_LEGACY_ENABLED",
    requiredRoles: ["iscednd.read"]
  },
  [Features.RISK_MANAGEMENT]: {
    userFlag: "mrmEnabled",
    envFlag: "VITE_IS_RISK_MANAGEMENT_ENABLED",
    requiredRoles: ["ISCE.read", "iscemrm.read"]
  },
  [Features.REPORTING]: {
    userFlag: null,
    envFlag: "enable_centralised_reporting",
    requiredRoles: ["ISCE.read", "isce.reporting.read"]
  },
  [Features.CARRIER_PERFORMANCE]: {
    userFlag: "carrierPerformanceEnabled",
    envFlag: "carrier_performance_enabled",
    requiredRoles: ["ISCE.read", "ISCE.write", "isce.carrierperformance.read", "isce.carrierperformance.write"]
  },
  [Features.UAM]: {
    userFlag: null,
    envFlag: "uam_enabled",
    requiredRoles: ["isce.admin"]
  },
  [Features.INVOICE_AUDIT]: {
    userFlag: null,
    envFlag: "enable_dnd_invoicing",
    requiredRoles: null
  },
  [Features.METERING]: {
    userFlag: null,
    envFlag: "VITE_IS_METERING_ENABLED",
    requiredRoles: ["isce.metering.read", "isce.metering.admin"]
  },
  [Features.BOOKINGS]: {
    userFlag: "bksEnabled",
    envFlag: null,
    requiredRoles: ["iscebks.read"]
  },
  [Features.TRACK_AND_TRACE]: {
    userFlag: null,
    envFlag: "enable_track_and_trace_view",
    requiredRoles: ["ISCE.read", "ISCE.write"]
  }
};
